import usePrivateLink from "../config/usePrivateLink";
import React, { useEffect, useState } from "react";
import {
  closeModal,
  ToastMain,
  SweetAlertShow,
  LoadingDiv,
} from "../master/Master";

import {
  ExpenseCategoryList,
  ExpenseCreate,
  ExpenseDelete,
  ExpenseUpdate,
} from "../../api/HostelExpenses";

import Header from "./HostelHeader";
import Nav from "../master/NavBar";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import Footer from "../main/footer";
import WelcomeBar from "./WelcomeBar";

export default function ExpenseCategory() {
  const hasAccess = usePrivateLink();

  const [loading, setLoading] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [modalData, setModalData] = useState(null);
  const [search, setSearch] = useState("");
  const [error, setError] = useState({});

  /* -----------------------------------------------------------
     Get Expense Categories
  ----------------------------------------------------------- */

  const getExpenseCategory = async () => {
    setLoading(true);

    try {
      const addUrl = `&search=${encodeURIComponent(search)}`;

      const response = await ExpenseCategoryList(addUrl);

      if (response?.data?.status) {
        setExpenses(response?.data?.data || []);
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to fetch expense categories.",
        );
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ||
          err?.response?.message ||
          err?.message ||
          "Something went wrong.",
      );
    } finally {
      setLoading(false);
    }
  };

  /* -----------------------------------------------------------
     Add Category
  ----------------------------------------------------------- */

  const AddExpenses = async (e) => {
    e.preventDefault();

    setLoading(true);
    setError({});

    try {
      const formData = new FormData(e.target);

      const response = await ExpenseCreate(formData);

      if (response?.data?.status) {
        setError({});
        closeModal("AddCategory");
        getExpenseCategory();

        ToastMain.fire({
          icon: "success",
          title: "Expense category created successfully!",
        });
      } else {
        if (response?.data?.errors) {
          setError(response.data.errors);
        }

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to create category.",
        );
      }
    } catch (err) {
      if (err?.response?.data?.errors) {
        setError(err.response.data.errors);
      }

      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ||
          err?.response?.message ||
          err?.message ||
          "Something went wrong.",
      );
    } finally {
      setLoading(false);
    }
  };

  /* -----------------------------------------------------------
     Update Category
  ----------------------------------------------------------- */

  const UpdateExpenses = async (e) => {
    e.preventDefault();

    setLoading(true);
    setError({});

    try {
      const formData = new FormData(e.target);

      const response = await ExpenseUpdate(formData);

      if (response?.data?.status) {
        setError({});
        closeModal("Edit");
        getExpenseCategory();

        ToastMain.fire({
          icon: "success",
          title: "Expense category updated successfully!",
        });
      } else {
        if (response?.data?.errors) {
          setError(response.data.errors);
        }

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to update category.",
        );
      }
    } catch (err) {
      if (err?.response?.data?.errors) {
        setError(err.response.data.errors);
      }

      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ||
          err?.response?.message ||
          err?.message ||
          "Something went wrong.",
      );
    } finally {
      setLoading(false);
    }
  };

  /* -----------------------------------------------------------
     Delete Category
  ----------------------------------------------------------- */

  const handleDelete = async (id) => {
    setLoading(true);

    try {
      const response = await ExpenseDelete(id);

      if (response?.data?.status) {
        getExpenseCategory();

        ToastMain.fire({
          icon: "success",
          title: "Expense category deleted successfully!",
        });
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to delete category.",
        );
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ||
          err?.response?.message ||
          err?.message ||
          "Something went wrong.",
      );
    } finally {
      setLoading(false);
    }
  };

  /* -----------------------------------------------------------
     Delete Confirmation
  ----------------------------------------------------------- */

  const confirmDelete = (item) => {
    Swal.fire({
      title: "Delete category?",
      html: `
        <div class="text-muted">
          You are about to delete
          <strong>${item?.title || "this category"}</strong>.
          <br/>
          This action cannot be undone.
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      customClass: {
        confirmButton: "btn btn-danger px-4",
        cancelButton: "btn btn-label-secondary px-4 ms-2",
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        handleDelete(item.id);
      }
    });
  };

  /* -----------------------------------------------------------
     Open Add Modal
  ----------------------------------------------------------- */

  const openAddModal = () => {
    setModalData(null);
    setError({});
  };

  /* -----------------------------------------------------------
     Open Edit Modal
  ----------------------------------------------------------- */

  const openEditModal = (item) => {
    setModalData(item);
    setError({});
  };

  /* -----------------------------------------------------------
     Search
  ----------------------------------------------------------- */

  useEffect(() => {
    const timer = setTimeout(() => {
      getExpenseCategory();
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  return (
    <>
      {/* =========================================================
          LOADING OVERLAY
      ========================================================= */}

      {loading && <LoadingDiv />}

      {/* =========================================================
          LAYOUT
      ========================================================= */}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid py-4">
                {/* =================================================
                    WELCOME BAR
                ================================================= */}

                <WelcomeBar />

                {/* =================================================
                    PAGE HEADER
                ================================================= */}

                <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-4">
                  <div>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-3 bg-label-primary"
                        style={{
                          width: 42,
                          height: 42,
                        }}
                      >
                        <i className="ri-price-tag-3-line fs-4 text-primary" />
                      </div>

                      <div>
                        <h4 className="mb-0 fw-bold">Expense Categories</h4>

                        <small className="text-muted">
                          Manage hostel expense categories
                        </small>
                      </div>
                    </div>
                  </div>

                  {hasAccess("/hostel/expense/category/add") && (
                    <button
                      type="button"
                      className="btn btn-primary d-flex align-items-center justify-content-center gap-2 px-4"
                      data-bs-toggle="modal"
                      data-bs-target="#AddCategory"
                      onClick={openAddModal}
                    >
                      <i className="ri-add-line fs-5" />
                      <span>Add Category</span>
                    </button>
                  )}
                </div>

                {/* =================================================
                    NAVIGATION TABS
                ================================================= */}

                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-body p-2">
                    <div className="d-flex flex-wrap gap-2">
                      {hasAccess("/hostel/expenses") && (
                        <Link
                          to="/hostel/expenses"
                          className="btn btn-sm btn-label-primary d-flex align-items-center gap-2 px-3"
                        >
                          <i className="ri-file-list-3-line" />
                          Transactions
                        </Link>
                      )}

                      {hasAccess("/hostel/expense/category") && (
                        <Link
                          to="/hostel/expense/category"
                          className="btn btn-sm btn-primary d-flex align-items-center gap-2 px-3"
                        >
                          <i className="ri-price-tag-3-line" />
                          Categories
                        </Link>
                      )}
                    </div>
                  </div>
                </div>

                {/* =================================================
                    SEARCH CARD
                ================================================= */}

                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-body">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                      <div>
                        <h6 className="mb-1 fw-bold">Search Categories</h6>

                        <small className="text-muted">
                          Find an expense category by title or description
                        </small>
                      </div>

                      <div
                        className="d-flex gap-2"
                        style={{
                          width: "100%",
                          maxWidth: "500px",
                        }}
                      >
                        <div className="input-group">
                          <span className="input-group-text bg-transparent">
                            <i className="ri-search-line" />
                          </span>

                          <input
                            type="text"
                            name="search"
                            id="search"
                            className="form-control"
                            placeholder="Search expense categories..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                          />

                          {search && (
                            <button
                              type="button"
                              className="btn btn-text-secondary"
                              onClick={() => setSearch("")}
                              title="Clear search"
                            >
                              <i className="ri-close-line" />
                            </button>
                          )}
                        </div>

                        <button
                          type="button"
                          className="btn btn-label-secondary"
                          onClick={() => setSearch("")}
                          title="Reset search"
                        >
                          <i className="ri-refresh-line" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* =================================================
                    CATEGORY TABLE
                ================================================= */}

                <div className="card border-0 shadow-sm">
                  {/* TABLE HEADER */}

                  <div className="card-header border-bottom py-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h5 className="mb-1 fw-bold">Expense Categories</h5>

                        <small className="text-muted">
                          {expenses?.length || 0}{" "}
                          {expenses?.length === 1 ? "category" : "categories"}{" "}
                          found
                        </small>
                      </div>

                      <div className="bg-label-primary rounded-pill px-3 py-2">
                        <i className="ri-price-tag-3-line me-1" />
                        <span className="fw-semibold text-primary">
                          {expenses?.length || 0}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0">
                        <thead>
                          <tr>
                            <th
                              className="text-center"
                              style={{ width: "80px" }}
                            >
                              #
                            </th>

                            <th>Category</th>

                            <th>Description</th>

                            <th
                              className="text-center"
                              style={{ width: "150px" }}
                            >
                              Actions
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {expenses?.length > 0 ? (
                            expenses.map((item, index) => (
                              <tr key={item.id}>
                                {/* INDEX */}

                                <td className="text-center">
                                  <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle bg-label-secondary fw-semibold"
                                    style={{
                                      width: 32,
                                      height: 32,
                                    }}
                                  >
                                    {index + 1}
                                  </span>
                                </td>

                                {/* TITLE */}

                                <td>
                                  <div className="d-flex align-items-center gap-3">
                                    <div
                                      className="d-flex align-items-center justify-content-center rounded-3 bg-label-primary flex-shrink-0"
                                      style={{
                                        width: 40,
                                        height: 40,
                                      }}
                                    >
                                      <i className="ri-price-tag-3-line text-primary fs-5" />
                                    </div>

                                    <div>
                                      <div className="fw-semibold">
                                        {item.title || "—"}
                                      </div>

                                      <small className="text-muted">
                                        Expense Category
                                      </small>
                                    </div>
                                  </div>
                                </td>

                                {/* DESCRIPTION */}

                                <td>
                                  <div
                                    className="text-muted"
                                    style={{
                                      maxWidth: "500px",
                                    }}
                                  >
                                    {item.description ? (
                                      item.description
                                    ) : (
                                      <span className="fst-italic opacity-75">
                                        No description provided
                                      </span>
                                    )}
                                  </div>
                                </td>

                                {/* ACTIONS */}

                                <td>
                                  <div className="d-flex justify-content-center gap-2">
                                    {hasAccess(
                                      "/hostel/expense/category/update",
                                    ) && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-icon btn-label-primary"
                                        data-bs-toggle="modal"
                                        data-bs-target="#Edit"
                                        onClick={() => openEditModal(item)}
                                        title="Edit category"
                                      >
                                        <i className="ri-pencil-line" />
                                      </button>
                                    )}

                                    {hasAccess(
                                      "/hostel/expense/category/delete",
                                    ) && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-icon btn-label-danger"
                                        onClick={() => confirmDelete(item)}
                                        title="Delete category"
                                      >
                                        <i className="ri-delete-bin-line" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="4" className="text-center py-5">
                                <div className="d-flex flex-column align-items-center">
                                  <div
                                    className="d-flex align-items-center justify-content-center rounded-circle bg-label-secondary mb-3"
                                    style={{
                                      width: 70,
                                      height: 70,
                                    }}
                                  >
                                    <i className="ri-price-tag-3-line fs-2 text-muted" />
                                  </div>

                                  <h6 className="fw-bold mb-1">
                                    No categories found
                                  </h6>

                                  <p className="text-muted mb-3">
                                    {search
                                      ? `No expense category matches "${search}".`
                                      : "No expense categories have been created yet."}
                                  </p>

                                  {search ? (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-label-primary"
                                      onClick={() => setSearch("")}
                                    >
                                      <i className="ri-refresh-line me-1" />
                                      Clear Search
                                    </button>
                                  ) : (
                                    hasAccess(
                                      "/hostel/expense/category/add",
                                    ) && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-primary"
                                        data-bs-toggle="modal"
                                        data-bs-target="#AddCategory"
                                        onClick={openAddModal}
                                      >
                                        <i className="ri-add-line me-1" />
                                        Add First Category
                                      </button>
                                    )
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <Footer />
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================
          ADD CATEGORY MODAL
      ========================================================= */}

      <div
        className="modal fade"
        id="AddCategory"
        tabIndex={-1}
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg">
            <form onSubmit={AddExpenses}>
              {/* HEADER */}

              <div className="modal-header bg-label-primary border-0 px-4 py-3">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-3 bg-primary text-white"
                    style={{
                      width: 42,
                      height: 42,
                    }}
                  >
                    <i className="ri-add-line fs-4" />
                  </div>

                  <div>
                    <h5 className="modal-title fw-bold mb-0">
                      Add Expense Category
                    </h5>

                    <small className="text-muted">
                      Create a new category for hostel expenses
                    </small>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                />
              </div>

              {/* BODY */}

              <div className="modal-body px-4 py-4">
                {/* TITLE */}

                <div className="mb-4">
                  <label htmlFor="add_title" className="form-label fw-semibold">
                    Category Title
                    <span className="text-danger ms-1">*</span>
                  </label>

                  <div className="input-group input-group-merge">
                    <span className="input-group-text">
                      <i className="ri-price-tag-3-line" />
                    </span>

                    <input
                      id="add_title"
                      type="text"
                      name="title"
                      className="form-control input-capitalize"
                      placeholder="e.g. Food, Electricity, Maintenance"
                      autoComplete="off"
                    />
                  </div>

                  {error?.title && (
                    <div className="text-danger small mt-1">
                      {Array.isArray(error.title)
                        ? error.title[0]
                        : error.title}
                    </div>
                  )}
                </div>

                {/* DESCRIPTION */}

                <div>
                  <label
                    htmlFor="add_description"
                    className="form-label fw-semibold"
                  >
                    Description
                  </label>

                  <div className="input-group input-group-merge">
                    <span className="input-group-text align-items-start pt-2">
                      <i className="ri-file-text-line" />
                    </span>

                    <textarea
                      id="add_description"
                      name="description"
                      className="form-control input-uppercase"
                      placeholder="Enter a short description..."
                      rows="3"
                    />
                  </div>

                  {error?.description && (
                    <div className="text-danger small mt-1">
                      {Array.isArray(error.description)
                        ? error.description[0]
                        : error.description}
                    </div>
                  )}
                </div>
              </div>

              {/* FOOTER */}

              <div className="modal-footer bg-label-primary border-0 px-4 py-3">
                <button
                  type="button"
                  className="btn btn-label-secondary px-4"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line me-1" />
                      Save Category
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* =========================================================
          EDIT CATEGORY MODAL
      ========================================================= */}

      <div className="modal fade" id="Edit" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg">
            <form onSubmit={UpdateExpenses}>
              {/* HEADER */}

              <div className="modal-header bg-label-primary border-0 px-4 py-3">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-3 bg-primary text-white"
                    style={{
                      width: 42,
                      height: 42,
                    }}
                  >
                    <i className="ri-pencil-line fs-4" />
                  </div>

                  <div>
                    <h5 className="modal-title fw-bold mb-0">
                      Edit Expense Category
                    </h5>

                    <small className="text-muted">
                      Update category information
                    </small>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                />
              </div>

              {/* BODY */}

              <div className="modal-body px-4 py-4">
                <input
                  type="hidden"
                  name="id"
                  value={modalData?.id || ""}
                  readOnly
                />

                {/* TITLE */}

                <div className="mb-4">
                  <label
                    htmlFor="edit_title"
                    className="form-label fw-semibold"
                  >
                    Category Title
                    <span className="text-danger ms-1">*</span>
                  </label>

                  <div className="input-group input-group-merge">
                    <span className="input-group-text">
                      <i className="ri-price-tag-3-line" />
                    </span>

                    <input
                      id="edit_title"
                      type="text"
                      name="title"
                      className="form-control input-capitalize"
                      placeholder="Enter category title"
                      defaultValue={modalData?.title || ""}
                      key={modalData?.id}
                    />
                  </div>

                  {error?.title && (
                    <div className="text-danger small mt-1">
                      {Array.isArray(error.title)
                        ? error.title[0]
                        : error.title}
                    </div>
                  )}
                </div>

                {/* DESCRIPTION */}

                <div>
                  <label
                    htmlFor="edit_description"
                    className="form-label fw-semibold"
                  >
                    Description
                  </label>

                  <div className="input-group input-group-merge">
                    <span className="input-group-text align-items-start pt-2">
                      <i className="ri-file-text-line" />
                    </span>

                    <textarea
                      id="edit_description"
                      name="description"
                      className="form-control input-uppercase"
                      placeholder="Enter a short description..."
                      rows="3"
                      defaultValue={modalData?.description || ""}
                      key={`description-${modalData?.id}`}
                    />
                  </div>

                  {error?.description && (
                    <div className="text-danger small mt-1">
                      {Array.isArray(error.description)
                        ? error.description[0]
                        : error.description}
                    </div>
                  )}
                </div>
              </div>

              {/* FOOTER */}

              <div className="modal-footer bg-label-primary border-0 px-4 py-3">
                <button
                  type="button"
                  className="btn btn-label-secondary px-4"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Updating...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line me-1" />
                      Update Category
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
